import React, { FC, useState, useEffect } from 'react'
import ExcelJS from 'exceljs'
// @ts-ignore
import classnames from 'classnames'
import { AdjustedRevenuesTablesProps, Cell, SheetData } from './AdjustedRevenuesTables.types'
import './AdjustedRevenuesTables.scss'
import { sanitizeHTML, APIENDPOINT, getCWPEnvironmentForURL } from '../../utils/common-utils'

export const AdjustedRevenuesTables: FC<AdjustedRevenuesTablesProps> = props => {
  const [sheetData, setSheetData] = useState<SheetData>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [apiError, setApiError] = useState<boolean>(false)

  const env = getCWPEnvironmentForURL(window.location.href)

  const {
    adjustedOperatingNetRevenuesDisclaimer,
    adjustedOperatingDisclaimer,
    mixShiftDisclaimer,
    roeDisclaimer,
    excelFilePath,
  } = props.content

  const isRichValue = (cellValue: ExcelJS.CellRichTextValue) => {
    return Boolean(cellValue && Array.isArray(cellValue.richText))
  }

  const richToHtml = (cellValue: ExcelJS.CellRichTextValue) => {
    let str = cellValue.richText
      .map(({ font = {}, text }) => {
        return [
          font.vertAlign === 'superscript' ? '<sup>' : '',
          font.vertAlign === 'subscript' ? '<sub>' : '',
          text,
          font.vertAlign === 'subscript' ? '</sub>' : '',
          font.vertAlign === 'superscript' ? '</sup>' : '',
        ].join('')
      })
      .join('')
    return str
  }

  useEffect(() => {
    const loadRevenuesWorkbook = async () => {
      setLoading(true)
      setApiError(false)
      const response = await fetch(`${APIENDPOINT(env).baseGallery}${excelFilePath}`)
      setLoading(false)
      const status = response.status
      if (status < 200 || status > 299) {
        setApiError(true)
        throw Error('Error response: ' + status)
      }

      const readBuffer = await response.arrayBuffer()
      const workbook = new ExcelJS.Workbook()
      return workbook.xlsx.load(readBuffer)
    }

    const setRevenuesData = async () => {
      try {
        const workbook = await loadRevenuesWorkbook()
        //Parsing sheets data
        let data: SheetData = {}
        workbook.eachSheet(worksheet => {
          const sheetName = worksheet.name
          const sheetRows: Array<Cell[]> = []

          worksheet.eachRow(row => {
            const cellsArray = row.model?.cells
            if (cellsArray) {
              const rowData = cellsArray.map(cell => {
                const bottomBorder = cell.style?.border?.bottom
                const cellValue = isRichValue(cell.value as ExcelJS.CellRichTextValue)
                  ? richToHtml(cell.value as ExcelJS.CellRichTextValue)
                  : cell.value
                return {
                  bottomBorder,
                  cellValue,
                }
              })
              sheetRows.push(rowData as Cell[])
            }
          })
          data[sheetName] = sheetRows
        })
        setSheetData(data)
      } catch (e) {
        console.log('Error in Fetching', e)
      }
    }
    setRevenuesData()
  }, [])

  const generateTable = (data: Array<Cell[]>, tableClasses?: string) => {
    return (
      <table className={tableClasses || ''}>
        <tbody>
          {data &&
            data.map((cell, rowIndex) => {
              return rowIndex === 0 ? (
                <tr className='disclosure-table-row-header'>
                  {cell.map(({ cellValue, bottomBorder }, cellIndex) => {
                    return cellIndex === 0 ? (
                      <th
                        key={cellValue}
                        className={classnames('disclosure-table-lalign', {
                          'border-thin': bottomBorder?.style === 'thin',
                          'border-medium': bottomBorder?.style === 'medium',
                        })}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(cellValue) || '',
                        }}></th>
                    ) : (
                      <th
                        key={cellValue}
                        scope='col'
                        className={classnames('disclosure-table-ralign', {
                          'border-thin': bottomBorder?.style === 'thin',
                          'border-medium': bottomBorder?.style === 'medium',
                        })}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(cellValue) || '',
                        }}></th>
                    )
                  })}
                </tr>
              ) : (
                <tr>
                  {cell.map(({ cellValue, bottomBorder }, cellIndex) => {
                    return cellIndex === 0 ? (
                      <th
                        key={cellValue}
                        scope='row'
                        className={classnames('disclosure-table-lalign u-textNormal', {
                          'border-thin': bottomBorder?.style === 'thin',
                          'border-bold': bottomBorder?.style === 'medium',
                        })}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(cellValue) || '',
                        }}></th>
                    ) : (
                      <td
                        key={cellValue}
                        className={classnames('disclosure-table-ralign', {
                          'border-thin': bottomBorder?.style === 'thin',
                          'border-bold': bottomBorder?.style === 'medium',
                        })}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(cellValue) || '',
                        }}></td>
                    )
                  })}
                </tr>
              )
            })}
        </tbody>
      </table>
    )
  }

  const generateDilutedShareTable = (earningsPerDilutedShare: Array<Cell[]>) => {
    return (
      <table>
        <tbody>
          {earningsPerDilutedShare && (
            <>
              <tr>
                <th
                  className='u-md-hidden u-sm-hidden u-textColorLight u-textBold u-textCenter diluted-share-title'
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(earningsPerDilutedShare[0][0].cellValue) || '',
                  }}></th>
              </tr>
              {earningsPerDilutedShare.map((cell, rowIndex) => {
                return rowIndex === 0 ? (
                  <tr className='disclosure-table-row-header'>
                    {cell.map(({ cellValue, bottomBorder }, cellIndex) => {
                      return cellIndex === 0 ? (
                        <th
                          key={cellValue}
                          className={classnames(
                            'disclosure-table-lalign u-hidden u-md-block u-sm-block',
                            {
                              'border-thin': bottomBorder?.style === 'thin',
                              'border-medium': bottomBorder?.style === 'medium',
                            }
                          )}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(cellValue) || '',
                          }}></th>
                      ) : (
                        <th
                          key={cellValue}
                          scope='col'
                          className={classnames('disclosure-table-ralign', {
                            'border-thin': bottomBorder?.style === 'thin',
                            'border-medium': bottomBorder?.style === 'medium',
                          })}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(cellValue) || '',
                          }}></th>
                      )
                    })}
                  </tr>
                ) : (
                  <tr>
                    {cell.map(({ cellValue, bottomBorder }, cellIndex) => {
                      return cellIndex === 0 ? (
                        <th
                          key={cellValue}
                          scope='row'
                          className={classnames(
                            'disclosure-table-lalign u-hidden u-md-block u-sm-block u-textNormal',
                            {
                              'border-thin': bottomBorder?.style === 'thin',
                              'border-bold': bottomBorder?.style === 'medium',
                              'u-border0 u-invisible': !cellValue,
                            }
                          )}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(cellValue) || '-',
                          }}></th>
                      ) : (
                        <td
                          key={cellValue}
                          className={classnames('disclosure-table-ralign', {
                            'border-thin': bottomBorder?.style === 'thin',
                            'border-bold': bottomBorder?.style === 'medium',
                            'u-border0 u-invisible': !cellValue,
                          })}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(cellValue) || '-',
                          }}></td>
                      )
                    })}
                  </tr>
                )
              })}
            </>
          )}
        </tbody>
      </table>
    )
  }

  return (
    <>
      {loading ? (
        <div className='u-flex u-flexJustifyCenter'>
          <div className='Loading'>
            <div className='Loading-indicator'></div>
            <div className='Loading-indicator'></div>
            <div className='Loading-indicator'></div>
          </div>
        </div>
      ) : null}
      {!loading && Object.keys(sheetData).length ? (
        <div id='disclosure-tables'>
          <section>
            <div className='u-paddingTop10gu'>
              <div className='disclosure-table u-sizeConstrained u-flex u-flexCol'>
                <div className='u-sizeFull u-marginBottom6gu'>
                  {sheetData.netRevenues && generateTable(sheetData.netRevenues)}
                  <div
                    className='u-marginTop2gu'
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(adjustedOperatingNetRevenuesDisclaimer) || '',
                    }}
                  />
                </div>
                <div className='u-sizeFull u-flex u-md-flexCol u-sm-flexCol'>
                  <div className='u-size9of12 u-md-sizeFull u-sm-sizeFull u-marginBottom6gu'>
                    {sheetData.earningsExceptPerShareAmount &&
                      generateTable(sheetData.earningsExceptPerShareAmount, 'u-marginBottom4gu')}
                    {sheetData.weightedShares && generateTable(sheetData.weightedShares)}
                    <div
                      className='u-marginTop2gu'
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(adjustedOperatingDisclaimer) || '',
                      }}
                    />
                  </div>
                  <div className='diluted-share-table-container u-size4of12 u-md-sizeFull u-sm-sizeFull u-marginBottom6gu u-paddingLeft4gu u-md-paddingLeft0 u-sm-paddingLeft0'>
                    {sheetData.earningsPerDilutedShare &&
                      generateDilutedShareTable(sheetData.earningsPerDilutedShare)}
                  </div>
                </div>
                <div className='u-sizeFull u-marginBottom6gu'>
                  {sheetData.ROE && generateTable(sheetData.ROE)}
                  <div
                    className='u-marginTop2gu'
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(roeDisclaimer) || '',
                    }}
                  />
                </div>
                <div className='u-sizeFull u-marginBottom6gu'>
                  {sheetData.mixShift && generateTable(sheetData.mixShift)}
                  <div className='u-sizeFull'>
                    <div
                      className='u-marginTop2gu'
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(mixShiftDisclaimer) || '',
                      }}></div>
                  </div>
                </div>
                {sheetData.AWMOperatingMargin &&
                  generateTable(sheetData.AWMOperatingMargin, 'u-marginBottom6gu')}
                {sheetData.assetManagementOperatingMargin &&
                  generateTable(sheetData.assetManagementOperatingMargin, 'u-marginBottom6gu')}
              </div>
            </div>
          </section>
        </div>
      ) : null}
      {!loading && apiError ? (
        <div className='State--error'>
          <div className='State-content'>
            <p className={`u-textSecondary`}>
              We are experiencing some difficulties fetching disclosure tables data. Please try
              again later.
            </p>
          </div>
        </div>
      ) : null}
    </>
  )
}
